import {inject, Injectable} from '@angular/core';
import {EMPTY, Observable} from 'rxjs';
import {ConnectApiService} from './connect-api.service';
import {expand, map, reduce} from 'rxjs/operators';

export type ConnectWorkflowNode = {
  id: string;
  type: string;
  ref?: string;
  label?: string;
  ['x-ui']?: {
    position?: {x: number; y: number};
    [key: string]: unknown;
  };
  [key: string]: unknown;
};

export type ConnectWorkflowCanvasBackgroundStyle = {
    fill?: string;
    stroke?: string;
    opacity?: number;
    radius?: number;
};

export type ConnectWorkflowCanvasBackground = {
    id: string;
    label?: string;
    position: { x: number; y: number };
    size: { width: number; height: number };
    style?: ConnectWorkflowCanvasBackgroundStyle;
    locked?: boolean;
};

export type ConnectWorkflowEdge = {
  id?: string;
  source: string;
  target: string;
  source_handle?: string;
  target_handle?: string;
  label?: string;
  [key: string]: unknown;
};

export type ConnectWorkflow = {
  id?: string;
  name: string;
    tags?: string[];
  version?: number;
  nodes?: ConnectWorkflowNode[];
  edges?: ConnectWorkflowEdge[];
    ['x-ui']?: {
        backgrounds?: ConnectWorkflowCanvasBackground[];
        [key: string]: unknown;
    };
  [key: string]: unknown;
};

export type ConnectRunStatus = 'queued' | 'running' | 'succeeded' | 'failed' | 'cancelled' | 'timed_out' | string;

export type ConnectRunStep = {
  id: string;
  node_id: string;
  type: string;
  status: ConnectRunStatus;
  attempt: number;
  retries?: number;
  started_at?: string;
  finished_at?: string;
  duration_ms?: number;
  input: unknown;
  output: unknown;
  error?: {code: string; message: string; retryable?: boolean; details?: Record<string, unknown>};
};

export type ConnectRun = {
  id: string;
  workflow_id: string;
  workflow_version?: number;
  status: ConnectRunStatus;
  created_at: string;
  started_at?: string;
  finished_at?: string;
  duration_ms?: number;
  attempt?: number;
  retries?: number;
  steps?: ConnectRunStep[];
};

export type ConnectRunContext = Record<string, unknown>;

export type ConnectRunReplaySummary = {
  plan_version?: number;
  node_count?: number;
  edge_count?: number;
  event_count?: number;
  last_event_type?: string;
  event_counts?: Record<string, number>;
};

export type ConnectWorkflowRun = {
  runId: string;
  workflowId: string;
  status: ConnectRunStatus;
  enqueuedAt: string;
};

export type ConnectConfiguredTriggerRun = {
  input: Record<string, unknown>;
  connectionBindings: Record<string, string>;
};

export type ConnectProviderFreeFieldKey =
  | 'full_name'
  | 'work_email'
  | 'company'
  | 'country'
  | 'question'
  | 'details';

export type ConnectProviderFreeFunctionDraft = {
  name: string;
  fields: ConnectProviderFreeFieldKey[];
};

@Injectable({
  providedIn: 'root',
})
export class ConnectWorkflowsService {
  private readonly api = inject(ConnectApiService);

    listWorkflows(params?: {
        includeDeleted?: boolean;
        onlyDeleted?: boolean;
        skip?: number;
        take?: number;
        tags?: string[];
    }): Observable<ConnectWorkflow[]> {
    const query: string[] = [];
        if (typeof params?.skip === 'number') {
            query.push(`skip=${encodeURIComponent(String(params.skip))}`);
        }
        if (typeof params?.take === 'number') {
            query.push(`take=${encodeURIComponent(String(params.take))}`);
        }
    if (params?.onlyDeleted) {
      query.push('only_deleted=true');
    } else if (params?.includeDeleted) {
      query.push('include_deleted=true');
    }
        if (params?.tags?.length) {
            params.tags.forEach(tag => query.push(`tags=${encodeURIComponent(tag)}`));
        }
    const qs = query.length ? `?${query.join('&')}` : '';
    return this.api.get<ConnectWorkflow[]>(`/api/v1/workflows${qs}`);
  }

    listAllWorkflows(params?: { includeDeleted?: boolean; onlyDeleted?: boolean }): Observable<ConnectWorkflow[]> {
        const pageSize = 100;
        const fetchPage = (skip: number) =>
            this.listWorkflows({
                skip,
                take: pageSize,
                includeDeleted: params?.includeDeleted,
                onlyDeleted: params?.onlyDeleted
            }).pipe(
                map(page => ({skip, page})),
            );

        return fetchPage(0).pipe(
            expand(({skip, page}) => (page.length >= pageSize ? fetchPage(skip + page.length) : EMPTY)),
            map(({page}) => page),
            reduce((acc, page) => [...acc, ...page], [] as ConnectWorkflow[]),
        );
    }

  getWorkflow(id: string): Observable<ConnectWorkflow> {
    return this.api.get<ConnectWorkflow>(`/api/v1/workflows/${id}`);
  }

  createWorkflow(w: ConnectWorkflow): Observable<ConnectWorkflow> {
    return this.api.post<ConnectWorkflow>(`/api/v1/workflows`, w);
  }

  createProviderFreeFunction(draft: ConnectProviderFreeFunctionDraft): Observable<ConnectWorkflow> {
    return this.api.post<ConnectWorkflow>(`/api/v1/workflows/provider-free-functions`, draft);
  }

  updateWorkflow(id: string, w: ConnectWorkflow): Observable<ConnectWorkflow> {
    return this.api.put<ConnectWorkflow>(`/api/v1/workflows/${id}`, w);
  }

  archiveWorkflow(id: string): Observable<void> {
    return this.api.delete<void>(`/api/v1/workflows/${id}`);
  }

  restoreWorkflow(id: string): Observable<{id: string; restored: boolean}> {
    return this.api.post<{id: string; restored: boolean}>(`/api/v1/workflows/${id}/restore`);
  }

  hardDeleteWorkflow(id: string): Observable<void> {
    return this.api.delete<void>(`/api/v1/workflows/${id}?hard=true`);
  }

  runWorkflow(id: string): Observable<ConnectWorkflowRun> {
    return this.api.post<ConnectWorkflowRun>(`/api/v1/workflows/${id}/run`);
  }

  runTrigger(id: string, triggerNodeId: string, input: Record<string, unknown>): Observable<ConnectWorkflowRun> {
    return this.api.post<ConnectWorkflowRun>(
      `/api/v1/workflows/${encodeURIComponent(id)}/triggers/${encodeURIComponent(triggerNodeId)}/run`,
      input,
    );
  }

  runConfiguredTrigger(
    id: string,
    triggerNodeId: string,
    request: ConnectConfiguredTriggerRun,
  ): Observable<ConnectWorkflowRun> {
    return this.api.post<ConnectWorkflowRun>(
      `/api/v1/workflows/${encodeURIComponent(id)}/triggers/${encodeURIComponent(triggerNodeId)}/configured-run`,
      {
        input: request.input,
        connection_bindings: request.connectionBindings,
      },
    );
  }

  listRuns(workflowId?: string): Observable<ConnectRun[]> {
    const qs = workflowId ? `?workflow_id=${encodeURIComponent(workflowId)}` : '';
    return this.api.get<ConnectRun[]>(`/api/v1/runs${qs}`);
  }

  getRun(runId: string): Observable<ConnectRun> {
    return this.api.get<ConnectRun>(`/api/v1/runs/${runId}`);
  }

  getRunContext(runId: string): Observable<ConnectRunContext> {
    return this.api.get<ConnectRunContext>(`/api/v1/runs/${runId}/context`);
  }

  getRunReplay(runId: string, options?: {includeWorkflow?: boolean}): Observable<ConnectRunReplaySummary> {
    const query = options?.includeWorkflow === false ? '' : '?include_workflow=true';
    return this.api.get<ConnectRunReplaySummary>(`/api/v1/runs/${runId}/replay${query}`);
  }

  rerun(runId: string): Observable<{runId: string; status: string; createdAt: string}> {
    return this.api.post<{runId: string; status: string; createdAt: string}>(`/api/v1/runs/${runId}/rerun`);
  }

  simulateNode(workflowId: string, nodeId: string, contextInput: unknown): Observable<{output: unknown}> {
    return this.api.post<{output: unknown}>(`/api/v1/workflows/${workflowId}/nodes/${nodeId}/simulate`, {
      context_input: contextInput ?? {},
    });
  }
}
